import { api } from './client';
import type {
  Character,
  CharacterWithRelations,
  CreateCharacterRequest,
  UpdateCharacterRequest,
  SetBoostsRequest,
  UpdateSkillsRequest,
  AddFeatRequest,
  CharacterFeat,
  CharacterSkill,
  SetPreparedSlotsRequest,
  PreparedSlotData,
  LevelUpRequest,
  LevelUpPreviewRequest,
  LevelUpPreview,
  InventoryItem,
  ContainerRule,
  ImportPathbuilderRequest,
  PathbuilderExport,
  CharacterCompanion,
  AddCompanionRequest,
  UpdateCompanionRequest,
  CharacterRitual,
  AddRitualRequest,
  UpdateRitualRequest,
  CharacterActiveAction,
  ActivateActionRequest,
  DeactivateActionRequest,
  UseActionRequest,
  ResetActionUsesRequest,
  LevelUpPlanData,
  SaveLevelUpPlanRequest,
} from '@charbuilder/shared';

export async function getCharacters(): Promise<Character[]> {
  const res = await api.get<Character[]>('/characters');
  return res.data;
}

export async function getCharacter(id: string): Promise<CharacterWithRelations> {
  const res = await api.get<CharacterWithRelations>(`/characters/${id}`);
  return res.data;
}

export async function createCharacter(data: CreateCharacterRequest): Promise<CharacterWithRelations> {
  const res = await api.post<CharacterWithRelations>('/characters', data);
  return res.data;
}

export async function updateCharacter(id: string, data: UpdateCharacterRequest): Promise<CharacterWithRelations> {
  const res = await api.patch<CharacterWithRelations>(`/characters/${id}`, data);
  return res.data;
}

export async function deleteCharacter(id: string): Promise<void> {
  await api.delete(`/characters/${id}`);
}

export async function setBoosts(characterId: string, data: SetBoostsRequest): Promise<CharacterWithRelations> {
  const res = await api.put<CharacterWithRelations>(`/characters/${characterId}/boosts`, data);
  return res.data;
}

export async function updateSkills(characterId: string, data: UpdateSkillsRequest): Promise<CharacterSkill[]> {
  const res = await api.put<CharacterSkill[]>(`/characters/${characterId}/skills`, data);
  return res.data;
}

export async function addFeat(characterId: string, data: AddFeatRequest): Promise<CharacterFeat> {
  const res = await api.post<CharacterFeat>(`/characters/${characterId}/feats`, data);
  return res.data;
}

export async function removeFeat(characterId: string, featId: string): Promise<void> {
  await api.delete(`/characters/${characterId}/feats/${featId}`);
}

export async function levelUp(characterId: string, data: LevelUpRequest = {}): Promise<CharacterWithRelations> {
  const res = await api.post<CharacterWithRelations>(`/characters/${characterId}/level-up`, data);
  return res.data;
}

export async function previewLevelUp(characterId: string, data?: LevelUpPreviewRequest): Promise<LevelUpPreview> {
  const res = await api.post<LevelUpPreview>(`/characters/${characterId}/level-up/preview`, data ?? {});
  return res.data;
}

// ── Level-Up Plan ──

export async function getLevelUpPlan(characterId: string): Promise<LevelUpPlanData | null> {
  const res = await api.get<LevelUpPlanData | null>(`/characters/${characterId}/level-up-plan`);
  return res.data;
}

export async function saveLevelUpPlan(characterId: string, data: SaveLevelUpPlanRequest): Promise<LevelUpPlanData> {
  const res = await api.put<LevelUpPlanData>(`/characters/${characterId}/level-up-plan`, data);
  return res.data;
}

export async function deleteLevelUpPlan(characterId: string): Promise<void> {
  await api.delete(`/characters/${characterId}/level-up-plan`);
}

export async function assignToCampaign(characterId: string, campaignId: string): Promise<Character> {
  const res = await api.post<Character>(`/characters/${characterId}/assign`, { campaignId });
  return res.data;
}

export async function setPreparedSlots(characterId: string, data: SetPreparedSlotsRequest): Promise<PreparedSlotData[]> {
  const res = await api.put<PreparedSlotData[]>(`/characters/${characterId}/prepared-slots`, {
    slots: data.slots,
    ...(data.source && { source: data.source }),
  });
  return res.data;
}

export async function expendSlot(characterId: string, slotId: string): Promise<PreparedSlotData> {
  const res = await api.post<PreparedSlotData>(`/characters/${characterId}/prepared-slots/${slotId}/expend`);
  return res.data;
}

export async function resetPreparedSlots(characterId: string): Promise<void> {
  await api.post(`/characters/${characterId}/prepared-slots/reset`);
}

export async function applySpellBlendingTrades(
  characterId: string,
  trades: { fromRank: number; fromCount: number; toRank: number; toCount: number }[],
): Promise<CharacterWithRelations> {
  const res = await api.post<CharacterWithRelations>(`/characters/${characterId}/spell-blending`, { trades });
  return res.data;
}

export async function buildMakeshiftStaff(
  characterId: string,
  spells: { name: string; rank: number }[],
): Promise<CharacterWithRelations> {
  const res = await api.post<CharacterWithRelations>(`/characters/${characterId}/staff-nexus/build`, { spells });
  return res.data;
}

export async function chargeMakeshiftStaff(
  characterId: string,
  preparedSlotId: string,
): Promise<CharacterWithRelations> {
  const res = await api.post<CharacterWithRelations>(`/characters/${characterId}/staff-nexus/charge`, { preparedSlotId });
  return res.data;
}

export async function takeRest(characterId: string): Promise<CharacterWithRelations> {
  const res = await api.post<CharacterWithRelations>(`/characters/${characterId}/rest`);
  return res.data;
}

export async function updateSpellRecord(characterId: string, spellRecordId: string, data: { isSignature?: boolean; isPrepared?: boolean }): Promise<void> {
  await api.patch(`/characters/${characterId}/spells/${spellRecordId}`, data);
}

export async function importFromPathbuilder(data: ImportPathbuilderRequest): Promise<CharacterWithRelations> {
  const res = await api.post<CharacterWithRelations>('/characters/import/pathbuilder', data);
  return res.data;
}

export async function fetchPathbuilderJson(input: string): Promise<PathbuilderExport> {
  // Accept full URL or just the numeric ID
  const match = input.match(/(\d+)\s*$/);
  if (!match) throw new Error('Could not extract Pathbuilder ID from input.');
  const res = await api.get<PathbuilderExport>('/characters/import/pathbuilder/fetch', { params: { id: match[1] } });
  return res.data;
}

export async function exportToPathbuilder(characterId: string): Promise<PathbuilderExport> {
  const res = await api.get<PathbuilderExport>(`/characters/${characterId}/export/pathbuilder`);
  return res.data;
}

export async function moveToContainer(characterId: string, itemId: string, containerId: string | null): Promise<InventoryItem> {
  const res = await api.patch<InventoryItem>(`/characters/${characterId}/inventory/${itemId}/container`, { containerId });
  return res.data;
}

export async function getContainerRules(): Promise<ContainerRule[]> {
  const res = await api.get<ContainerRule[]>('/game-data/container-rules');
  return res.data;
}

export async function toggleItemIdentified(characterId: string, itemId: string): Promise<InventoryItem> {
  const res = await api.patch<InventoryItem>(`/characters/${characterId}/inventory/${itemId}/identify`);
  return res.data;
}

export async function activateBattleForm(characterId: string, battleFormId: string): Promise<CharacterWithRelations> {
  const res = await api.post<CharacterWithRelations>(`/characters/${characterId}/battle-form`, { battleFormId });
  return res.data;
}

export async function deactivateBattleForm(characterId: string): Promise<CharacterWithRelations> {
  const res = await api.delete<CharacterWithRelations>(`/characters/${characterId}/battle-form`);
  return res.data;
}

// ── Companions ──

export async function addCompanion(characterId: string, data: AddCompanionRequest): Promise<CharacterCompanion> {
  const res = await api.post<CharacterCompanion>(`/characters/${characterId}/companions`, data);
  return res.data;
}

export async function updateCompanion(characterId: string, companionId: string, data: UpdateCompanionRequest): Promise<CharacterCompanion> {
  const res = await api.patch<CharacterCompanion>(`/characters/${characterId}/companions/${companionId}`, data);
  return res.data;
}

export async function deleteCompanion(characterId: string, companionId: string): Promise<void> {
  await api.delete(`/characters/${characterId}/companions/${companionId}`);
}

// ── Rituals ──

export async function addRitual(characterId: string, data: AddRitualRequest): Promise<CharacterRitual> {
  const res = await api.post<CharacterRitual>(`/characters/${characterId}/rituals`, data);
  return res.data;
}

export async function updateRitual(characterId: string, ritualId: string, data: UpdateRitualRequest): Promise<CharacterRitual> {
  const res = await api.patch<CharacterRitual>(`/characters/${characterId}/rituals/${ritualId}`, data);
  return res.data;
}

export async function deleteRitual(characterId: string, ritualId: string): Promise<void> {
  await api.delete(`/characters/${characterId}/rituals/${ritualId}`);
}

// ── Active Actions ──

export async function activateAction(characterId: string, data: ActivateActionRequest) {
  const res = await api.post<{ activated: CharacterActiveAction; deactivated: string[] }>(`/characters/${characterId}/active-actions/activate`, data);
  return res.data;
}

export async function deactivateAction(characterId: string, data: DeactivateActionRequest) {
  const res = await api.post<CharacterActiveAction>(`/characters/${characterId}/active-actions/deactivate`, data);
  return res.data;
}

export async function useAction(characterId: string, data: UseActionRequest) {
  const res = await api.post<CharacterActiveAction>(`/characters/${characterId}/active-actions/use`, data);
  return res.data;
}

export async function resetActionUses(characterId: string, data: ResetActionUsesRequest) {
  const res = await api.post<CharacterActiveAction>(`/characters/${characterId}/active-actions/reset-uses`, data);
  return res.data;
}

export async function resetAllActionUses(characterId: string) {
  const res = await api.post(`/characters/${characterId}/active-actions/reset-all-uses`);
  return res.data;
}

export async function deactivateAllActions(characterId: string) {
  const res = await api.post(`/characters/${characterId}/active-actions/deactivate-all`);
  return res.data;
}

export async function markReactionUsed(characterId: string, actionName: string) {
  const res = await api.post(`/characters/${characterId}/active-actions/mark-reaction-used`, { actionName });
  return res.data;
}

export async function resetReactions(characterId: string) {
  const res = await api.post(`/characters/${characterId}/active-actions/reset-reactions`);
  return res.data;
}
